import { useEffect, useState } from 'react'
import moment from 'moment'
import { format, isValid, parseISO } from 'date-fns'
import { Edit05, Trash01, User01, XClose } from '@untitled-ui/icons-react'
import { MyButton, MyConfirmModal, MyLoadingData } from '@interstellar-component'
import AuthImage from '@src/components/AuthImage'
import { useEmploymentSettings } from './Context'

function formatRupiah(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(String(value).replace(/\D/g, ''))
  if (Number.isNaN(n)) return ''
  return `Rp${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

function formatWhen(iso) {
  if (!iso) return null
  const d = typeof iso === 'string' ? parseISO(iso) : new Date(iso)
  if (!isValid(d)) return typeof iso === 'string' ? iso : null
  return format(d, 'dd MMM yyyy • HH:mm')
}

/**
 * Full date with an English month name, e.g. `26 March 2030`.
 *
 * This used to import 'moment/locale/id' and switch to it — and importing a
 * moment locale file also makes it moment's GLOBAL default, so every
 * moment().format('… MMM …') across the app rendered Indonesian month names.
 */
function formatConsentExpiry(value) {
  if (value == null || value === '') return null
  const m = moment(value)
  if (!m.isValid()) return String(value)
  return m.locale('en').format('D MMMM YYYY')
}

function formatRepeatEveryLabel(value) {
  if (value === null || value === undefined || value === '') return '—'
  const text = String(value)
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function pickActor(data, kind) {
  const rel = kind === 'updated' ? data?.updater ?? data?.updated_by : data?.creator ?? data?.created_by
  const direct = rel ?? data?.[`${kind}_by`]
  if (direct && typeof direct === 'object') {
    return {
      name: direct.name ?? direct.full_name ?? '—',
      role: direct.role ?? direct.job_title ?? direct.position ?? '',
      avatar: direct.avatar_full_url ?? direct.photo_url,
    }
  }
  if (typeof direct === 'string') {
    return { name: direct, role: '', avatar: null }
  }
  return {
    name: data?.[`${kind}_by_name`] ?? null,
    role: data?.[`${kind}_by_role`] ?? '',
    avatar: data?.[`${kind}_by_avatar`] ?? null,
  }
}

export default function LevelDetailsSlider({
  open,
  data,
  loading,
  onClose,
  onEdit,
  inModalSlider = false,
}) {
  const { deleteEmploymentLevels } = useEmploymentSettings()
  const [isVisible, setIsVisible] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  useEffect(() => {
    if (open) {
      const frame = requestAnimationFrame(() => setIsVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setIsVisible(false)
    return undefined
  }, [open])

  const animateClose = () => {
    if (inModalSlider) {
      onClose()
      return
    }
    setIsVisible(false)
    setTimeout(onClose, 300)
  }

  const handleConfirmDelete = async () => {
    setDeleteConfirmOpen(false)
    if (data?.id != null) {
      try {
        await deleteEmploymentLevels([data.id])
        animateClose()
      } catch {
        /* myToaster in context */
      }
    } else {
      animateClose()
    }
  }

  const levelName = data?.name ?? data?.level ?? '—'
  const salaryFrom = formatRupiah(data?.salary_from) || '—'
  const salaryTo = formatRupiah(data?.salary_to) || '—'
  const rawConsent = data?.consent_expiry ?? data?.consentExpiry
  const consent =
    rawConsent == null || rawConsent === ''
      ? '—'
      : formatConsentExpiry(rawConsent) ?? '—'
  const repeat = formatRepeatEveryLabel(data?.repeat_every ?? data?.repeatEvery)

  const lastMod = pickActor(data, 'updated')
  const createdBy = pickActor(data, 'created')
  const updatedAt = formatWhen(data?.updated_at)
  const createdAt = formatWhen(data?.created_at)

  if (inModalSlider) {
    if (!open) return null
  } else if (!open && !isVisible) {
    return null
  }

  const standalonePanelClass =
    'flex w-[375px] flex-col border-l border-gray-200 bg-white shadow-xl font-inter'
  const embeddedPanelClass =
    'flex h-full min-h-0 w-[375px] flex-col overflow-hidden bg-white font-inter'

  const panelBody = (
    <>
      <header className="relative px-6 py-6">
        <button
          type="button"
          onClick={animateClose}
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-light/400 hover:bg-gray-light/50"
        >
          <XClose size={24} stroke="currentColor" />
        </button>
      </header>
      <div className="flex flex-1 flex-col overflow-y-auto">
            <div className="border-b border-gray-200 px-6 pb-4">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">{levelName}</h2>
                <p className="text-sm text-gray-500 font-medium">Employment Level</p>
              </div>
            </div>

            {loading ? (
              <div className="flex min-h-[280px] flex-1 items-center justify-center px-6">
                <MyLoadingData />
              </div>
            ) : (
              <>
                <div className="mt-8 px-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-25 shadow-xs">
                    <div className="px-5 pb-2 pt-3">
                      <h3 className="text-sm font-semibold text-gray-900">Information</h3>
                    </div>
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs">
                      <div className="flex items-center border-b border-gray-200 last:border-b-0">
                        <div className="w-[180px] shrink-0 px-6 py-4">
                          <span className="text-sm font-normal text-gray-600">Employment Level Name</span>
                        </div>
                        <div className="min-w-0 flex-1 px-6 py-4 text-right">
                          <span className="block max-w-full text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">
                            {levelName}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center border-b border-gray-200 last:border-b-0">
                        <div className="w-[180px] shrink-0 px-6 py-4">
                          <span className="text-sm font-normal text-gray-600">Salary Range (from)</span>
                        </div>
                        <div className="min-w-0 flex-1 px-6 py-4 text-right">
                          <span className="block max-w-full text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">
                            {salaryFrom}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center border-b border-gray-200 last:border-b-0">
                        <div className="w-[180px] shrink-0 px-6 py-4">
                          <span className="text-sm font-normal text-gray-600">Salary Range (to)</span>
                        </div>
                        <div className="min-w-0 flex-1 px-6 py-4 text-right">
                          <span className="block max-w-full text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">
                            {salaryTo}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 px-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-25 shadow-xs">
                    <div className="px-5 pb-2 pt-3">
                      <h3 className="text-sm font-semibold text-gray-900">Screening Rules</h3>
                    </div>
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs">
                      <div className="flex items-center border-b border-gray-200 last:border-b-0">
                        <div className="w-[180px] shrink-0 px-6 py-4">
                          <span className="text-sm font-normal text-gray-600">Consent Expiry</span>
                        </div>
                        <div className="flex-1 px-6 py-4 text-right">
                          <span className="text-sm font-medium text-gray-900">{consent}</span>
                        </div>
                      </div>
                      <div className="flex items-center border-b border-gray-200 last:border-b-0">
                        <div className="w-[180px] shrink-0 px-6 py-4">
                          <span className="text-sm font-normal text-gray-600">Repeat Every</span>
                        </div>
                        <div className="flex-1 px-6 py-4 text-right">
                          <span className="text-sm font-medium text-gray-900">{repeat}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {(updatedAt || createdAt || lastMod.name || createdBy.name) && (
                  <div className="mt-8 px-4 pb-8">
                    <div className="rounded-xl border border-gray-200 bg-gray-25 shadow-xs">
                      <div className="px-5 pb-2 pt-3">
                        <h3 className="text-sm font-semibold text-gray-900">Changes</h3>
                      </div>
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs">
                        {/* Sequelize always seeds updated_at = created_at on insert,
                            so updatedAt alone is not a "has been edited" signal.
                            Gate this block on the updater relation being present —
                            the BE only sets updated_by on actual updates. */}
                        {data?.updater && updatedAt && (
                          <div className="flex items-center border-b border-gray-200 last:border-b-0">
                            <div className="w-[120px] shrink-0 self-start px-6 py-4 text-left">
                              <span className="text-sm font-normal text-gray-600">Last modified</span>
                            </div>
                            <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_40px] items-center gap-3 px-6 py-4">
                              <div className="flex min-w-0 flex-1 flex-col items-end text-right">
                                <span className="max-w-full text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">
                                  {lastMod.name ?? '—'}
                                </span>
                                {!!lastMod.role && (
                                  <span className="max-w-full text-sm font-normal text-gray-600 [overflow-wrap:anywhere]">
                                    {lastMod.role.name}
                                  </span>
                                )}
                                <span className="mt-0.5 max-w-full whitespace-nowrap text-xs font-normal text-gray-500">{updatedAt}</span>
                              </div>
                              {lastMod.avatar ? (
                                <AuthImage
                                  src={lastMod.avatar}
                                  alt=""
                                  className="h-10 w-10 shrink-0 rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                                  <User01 className="h-5 w-5" stroke="currentColor" />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {createdAt && (
                          <div className="flex items-center border-b border-gray-200 last:border-b-0">
                            <div className="w-[120px] shrink-0 self-start px-6 py-4 text-left">
                              <span className="text-sm font-normal text-gray-600">Created</span>
                            </div>
                            <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_40px] items-center gap-3 px-6 py-4">
                              <div className="flex min-w-0 flex-1 flex-col items-end text-right">
                                <span className="max-w-full text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">
                                  {createdBy.name ?? '—'}
                                </span>
                                {!!createdBy.role && (
                                  <span className="max-w-full text-sm font-normal text-gray-600 [overflow-wrap:anywhere]">
                                    {createdBy.role.name}
                                  </span>
                                )}
                                <span className="mt-0.5 max-w-full whitespace-nowrap text-xs font-normal text-gray-500">{createdAt}</span>
                              </div>
                              {createdBy.avatar ? (
                                <AuthImage
                                  src={createdBy.avatar}
                                  alt=""
                                  className="h-10 w-10 shrink-0 rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                                  <User01 className="h-5 w-5" stroke="currentColor" />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border-t border-gray-200 bg-white px-6 py-4">
            <div className="flex items-center justify-between">
              <MyButton
                color="error"
                size="sm"
                variant="text"
                disabled={loading || !data?.id}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                <Trash01 className="h-4 w-4 mr-1.5" stroke="currentColor" />
                Delete
              </MyButton>
              <MyButton
                color="secondary"
                size="md"
                variant="outlined"
                disabled={loading || !data}
                onClick={onEdit}
              >
                <Edit05 className="h-4 w-4 mr-1.5" stroke="currentColor" />
                Edit
              </MyButton>
            </div>
          </div>
    </>
  )

  return (
    <>
      <MyConfirmModal
        negativeActionWord="Cancel"
        positiveActionWord="Delete"
        open={deleteConfirmOpen}
        title="Delete level"
        message={`Are you sure you want to delete "${levelName}"? This action cannot be undone.`}
        icon={<Trash01 className="text-error-600" />}
        bgColor="bg-error-100"
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        negativeActionWord="Cancel"
        positiveActionWord="Delete"
        positiveButtonColor="error"
        forceBlur
      />

      {inModalSlider ? (
        <div className={embeddedPanelClass}>{panelBody}</div>
      ) : (
        <>
          <div
            role="presentation"
            className={`fixed inset-0 z-40 bg-gray-950/70 backdrop-blur-md transition-opacity duration-300 ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={animateClose}
          />

          <div
            className={`fixed right-0 top-0 z-50 flex h-full items-stretch pl-10 transition-transform duration-300 ease-in-out ${
              isVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className={standalonePanelClass}>{panelBody}</div>
          </div>
        </>
      )}
    </>
  )
}
